<script setup lang="ts">
import type { GakugeiinItemProps } from '@/types/appTypes'

const props = withDefaults(defineProps<GakugeiinItemProps>(), {})

const app = useNuxtApp()
const storeIndex = useStoreIndex()
const storeUser = useStoreUser()

const onClickDetail = () => {
  const to = `/gakugeiin/${props.item.article_slug}`
  if (storeUser.state.isLogin) {
    navigateTo(to)
  } else {
    storeUser.setLoginRedirectPath(to)
    navigateTo('/login')
  }
}

let desc = ''
if (props.item.custom.description) {
  desc = app.$trimEnterTrue(props.item.custom.description)
}
</script>

<template>
  <div
    class="c-gakugeiinItem"
    v-bind:class="{ 'is-pr': item.is_pr }"
  >
    <div class="c-gakugeiinItem_head">
      <span
        v-if="item.is_pr"
        class="c-gakugeiinItem_head_tag"
      >
        PR
      </span>
      <span class="c-gakugeiinItem_head_title c-clampLines is-clampline3">
        <nuxt-link
          v-bind:to="`/gakugeiin/${item.article_slug}`"
        >
          {{ item.title }}
        </nuxt-link>
      </span>
    </div>
    <div class="c-gakugeiinItem_place">
      <div
        v-if="item.custom && item.custom.jis_no"
        class="c-gakugeiinItem_place_city"
      >
        {{ storeIndex.getPrefName(item.custom.jis_no) }}
      </div>
      <div
        v-if="item.custom && item.custom.location"
        class="c-gakugeiinItem_place_label"
      >
        勤務地
      </div>
      <div
        v-if="item.custom && item.custom.location"
        class="c-gakugeiinItem_place_location"
      >
        {{ item.custom.location }}
      </div>
    </div>
    <div
      v-if="desc"
      class="c-gakugeiinItem_description"
    >
      <div class="c-gakugeiinItem_description_box">
        <p class="c-gakugeiinItem_description_box_text">
          {{ desc }}
        </p>
      </div>
    </div>
    <div class="c-gakugeiinItem_schedule">
      <div
        v-if="item.open_date"
        class="c-gakugeiinItem_schedule_open"
      >
        <div class="c-gakugeiinItem_schedule_open_label">
          情報の公開日
        </div>
        <div class="c-gakugeiinItem_schedule_open_data">
          {{ app.$getPeriodDayLabelNoDD(item.open_date) }}
        </div>
      </div>
      <div
        v-if="item.custom && item.term_to"
        class="c-gakugeiinItem_schedule_close"
      >
        <div class="c-gakugeiinItem_schedule_close_label">
          締切
        </div>
        <div class="c-gakugeiinItem_schedule_close_data">
          {{ app.$getPeriodDayLabelNoDD(item.term_to) }}
        </div>
      </div>
    </div>
    <div class="c-gakugeiinItem_btnRow">
      <div
        class="c-gakugeiinItem_btnRow_btn"
        v-on:click="onClickDetail()"
      >
        詳しい情報を見る
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-gakugeiinItem {
  width: 100%;
  margin: 20px 0;
  padding: 20px;
  border: solid 2px #ebebeb;
  background-color: #fff;
  font-size: 14px;

  &.is-pr {
    border-color: var(--color_primary);
  }

  &_head {
    display: flex;
    align-items: flex-start;

    @include responsive.mq_down(sp) {
      display: block;
    }

    &_tag {
      display: inline-block;
      flex-shrink: 0;
      width: 76px;
      margin-right: 28px;
      padding: 0;
      background-color: #231815;
      color: #fff;
      line-height: 24px;
      text-align: center;

      @include responsive.mq_down(sp) {
        margin: 0 0 10px;
        transform: none;
      }
    }

    &_title {
      flex-grow: 1;
      margin-top: -2px;
      padding: 0 0 12px;
      border-bottom: 1px solid #ebebeb;
      font-size: 18px;
      font-weight: 700;
      line-height: 28px;

      @include responsive.mq_down(sp) {
        display: block;
      }

      a {
        color: #252525;
        font-size: 18px;
        font-weight: 700;
      }
    }
  }

  &_place {
    display: flex;
    align-items: center;
    margin-top: 20px;

    &_city {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 60px;
      min-width: 60px;
      height: 60px;
      margin-right: 30px;
      background-color: var(--color_background_whiteblue);
      color: #7d7d7d;
      font-size: 13px;
      font-weight: bold;
      line-height: 33px;
      white-space: nowrap;

      @include responsive.mq_down(sp) {
        margin-right: 15px;
      }
    }

    &_label {
      // margin-left: 30px;
      color: #7d7d7d;
      font-size: 16px;
      font-weight: bold;
      white-space: nowrap;
    }

    &_location {
      margin-left: 20px;
      font-size: 16px;
      font-weight: bold;

      @include responsive.mq_down(sp) {
        margin-left: 15px;
      }
    }
  }

  &_description {
    margin-top: 15px;
    background-color: #f8fafb;

    @include responsive.mq_down(sp) {
      margin-top: 10px;
    }

    &_box {
      padding: 15px;

      @include responsive.mq_down(sp) {
        padding: 10px;
      }

      &_text {
        display: -webkit-box;
        overflow: hidden;
        font-size: 14px;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;

        @include responsive.mq_down(sp) {
          -webkit-line-clamp: 4;
        }
      }
    }
  }

  &_schedule {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    margin-top: 15px;

    @include responsive.mq_down(sp) {
      flex-direction: column;
      align-items: flex-end;
      margin-top: 10px;
    }

    &_open {
      display: flex;
      margin-right: 5px;

      &_label {
        margin-right: 10px;
        color: #7d7d7d;
        font-size: 14px;
        font-weight: bold;
      }

      &_data {
        // margin-right: 35px;
        font-size: 14px;
        font-weight: bold;
      }
    }

    &_close {
      display: flex;
      margin-right: 5px;
      margin-left: 30px;

      &_label {
        margin-right: 10px;
        color: #7d7d7d;
        font-size: 14px;
        font-weight: bold;
      }

      &_data {
        color: #c20000;
        font-size: 14px;
        font-weight: bold;
      }
    }
  }

  &_btnRow {
    display: flex;
    flex-direction: row-reverse;
    margin-top: 20px;

    @include responsive.mq_down(sp) {
      margin-top: 15px;
    }

    &_btn {
      min-width: 200px;
      padding: 8px 10px 10px;
      background-color: var(--color_primary);
      color: #fff;
      font-size: 14px;
      text-align: center;
      cursor: pointer;
    }
  }
}
</style>
